import moment from 'moment-timezone';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { IntelligenceFilterId } from 'containers/Intelligence/types';

import { humaniseSeriesKey } from './chartAdapters';
import { OrbitCohortApplied, OrbitCohortListTag, OrbitCohortTag } from './cohortTypes';
import { TOrbit } from './i18n/TModules';

/** The order the chips read in: the activity window first, then the payload's band tags. */
export const COHORT_TAG_ORDER: OrbitCohortTag[] = [
  'activity_window_days',
  'lifecycle_stage_in',
  'lifecycle_stage_not_in',
  'current_clv_band_in',
  'predicted_clv_band_in',
  'holding_count_band_in',
  'product_relationship_in',
  'disengagement_risk_band_in'
];

/**
 * The Customer Insights filter category whose option copy already names a dimension's values. A
 * dimension with no category here (holding count, disengagement risk) is humanised from its key.
 */
const VALUE_VOCABULARY: Record<string, IntelligenceFilterId> = {
  lifecycle_stage: 'lifecycle',
  current_clv_band: 'customerValue',
  predicted_clv_band: 'customerValue',
  product_relationship: 'productRelationship'
};

export const cohortTagValues = (raw: OrbitCohortListTag): string[] => {
  if (raw === null) {
    return [];
  }

  const values = Array.isArray(raw) ? raw : raw.split(',');

  return values.map(value => value.trim()).filter(value => value !== '');
};

const dimensionOf = (tag: OrbitCohortTag): string => tag.replace(/_(not_)?in$/, '');

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One cohort value in the dashboard's own words, e.g. `very_high` -> `Very High`. */
export const cohortValueLabel = (t: Translate, dimension: string, value: string): string => {
  const category = VALUE_VOCABULARY[dimension];
  const fallback = humaniseSeriesKey(value);

  return category === undefined
    ? fallback
    : t(`${TCustomerInsights}.Filters.Categories.${category}.Options.${value}`, {
        defaultValue: fallback
      });
};

export interface OrbitCohortChip {
  tag: string;
  label: string;
}

const KNOWN_TAGS = new Set<string>(COHORT_TAG_ORDER);

const toListTag = (raw: unknown): OrbitCohortListTag => {
  if (Array.isArray(raw)) {
    return raw.map(String);
  }

  return typeof raw === 'string' || typeof raw === 'number' ? String(raw) : null;
};

/*
  A tag this build does not know is still a filter the backend applied, so it is drawn after the
  known ones with a humanised name rather than dropped: hiding it would present a narrower cohort
  as the whole question.
*/
const unknownTagChips = (applied: OrbitCohortApplied): OrbitCohortChip[] =>
  Object.keys(applied)
    .filter(tag => !KNOWN_TAGS.has(tag))
    .flatMap((tag): OrbitCohortChip[] => {
      const values = cohortTagValues(
        toListTag((applied as unknown as Record<string, unknown>)[tag])
      );

      if (values.length === 0) {
        return [];
      }

      const dimension = humaniseSeriesKey(tag.replace(/_(not_)?in$/, ''));
      const name = /_not_in$/.test(tag) ? `${dimension} excluded` : dimension;

      return [{ tag, label: `${name}: ${values.map(humaniseSeriesKey).join(', ')}` }];
    });

/**
 * A chip per applied tag; an unapplied (`null` / `''`) tag draws nothing. Known tags read in a
 * fixed order, then any tag the backend added that this build has no copy for.
 */
export const cohortChips = (t: Translate, applied: OrbitCohortApplied): OrbitCohortChip[] => [
  ...knownTagChips(t, applied),
  ...unknownTagChips(applied)
];

const knownTagChips = (t: Translate, applied: OrbitCohortApplied): OrbitCohortChip[] =>
  COHORT_TAG_ORDER.flatMap((tag): OrbitCohortChip[] => {
    if (tag === 'activity_window_days') {
      const days = Number(applied.activity_window_days);

      return applied.activity_window_days !== null &&
        applied.activity_window_days.trim() !== '' &&
        Number.isFinite(days)
        ? [{ tag, label: t(`${TOrbit}.cohort.activityWindow`, { count: days }) }]
        : [];
    }

    const values = cohortTagValues(applied[tag]);

    if (values.length === 0) {
      return [];
    }

    const dimension = dimensionOf(tag);

    return [
      {
        tag,
        label: t(`${TOrbit}.cohort.tag`, {
          dimension: t(`${TOrbit}.cohort.dimensions.${tag}`),
          values: values.map(value => cohortValueLabel(t, dimension, value)).join(', ')
        })
      }
    ];
  });

/** `2026-07-01T00:00:00Z` or `2026-07-01` -> `1 Jul 2026`; anything unparseable -> `null`. */
export const formatCohortDate = (raw: string | null): string | null => {
  if (raw === null || raw.trim() === '') {
    return null;
  }

  const date = moment.utc(raw, [moment.ISO_8601, 'YYYY-MM-DD'], true);

  return date.isValid() ? date.format('D MMM YYYY') : null;
};
